import React, { useState, useEffect } from 'react';
import api from '../services/api';
import DocumentList from '../components/DocumentList';
import UserDashboard from '../pages/UserDashboard';
import ConsultantSidebar from '../components/ConsultantSidebar';
import styles from '../styles/App.module.css';

const SIDEBAR_WIDTH = 260;
const NAVBAR_HEIGHT = 60;
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ConsultantDashboard = () => {
  const [section, setSection] = useState('clients');
  const [consultant, setConsultant] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);

  // NBR password inputs and per-row saving/editing state
  const [nbrInputs, setNbrInputs] = useState({});
  const [savingMap, setSavingMap] = useState({});
  const [editingMap, setEditingMap] = useState({}); // { [userId]: true }

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const p = await api.get('/users/profile');
        setConsultant(p.data || null);

        const list = await api.get('/users/list');
        const all = list.data || [];
        const assigned = (p.data && p.data.id) ? all.filter(u => Number(u.consultant_id) === Number(p.data.id)) : [];
        setClients(assigned);

        // initialize nbr password inputs cache for clients
        const nbrs = {};
        (assigned || []).forEach(u => {
          nbrs[u.id] = u.nbr_password ?? u.nbrPassword ?? u.password ?? '';
        });
        setNbrInputs(nbrs);
      } catch (err) {
        console.error('Failed to load consultant data', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateNbrPassword = async (userId) => {
    const pwd = nbrInputs[userId] ?? '';
    setSavingMap(prev => ({ ...prev, [userId]: true }));
    try {
      // JSON route (no multipart) implemented on backend
      const res = await api.put(`/users/${userId}/password`, { nbr_password: pwd });
      const saved = res?.data?.nbr_password ?? pwd;
      // update local clients list and exit edit mode
      setClients(prev => prev.map(c => c.id === userId ? { ...c, nbr_password: saved } : c));
      setNbrInputs(prev => ({ ...prev, [userId]: saved }));
      setEditingMap(prev => ({ ...prev, [userId]: false }));
    } catch (err) {
      console.error('Failed to update NBR password', err);
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || 'Failed to update NBR password';
      if (status === 404) {
        alert('Update endpoint not found (404). Ensure PUT /users/:id/password exists on the backend.');
      } else {
        alert(msg);
      }
    } finally {
      setSavingMap(prev => { const copy = { ...prev }; delete copy[userId]; return copy; });
    }
  };

  const startEditing = (userId) => {
    setEditingMap(prev => ({ ...prev, [userId]: true }));
    // ensure nbrInputs has a value so input is controlled and focus stable
    setNbrInputs(prev => ({ ...prev, [userId]: prev[userId] ?? (clients.find(c => c.id === userId)?.nbr_password ?? '') }));
  };
  const cancelEditing = (userId) => {
    // revert input to saved value and exit edit mode
    setNbrInputs(prev => ({ ...prev, [userId]: clients.find(c => c.id === userId)?.nbr_password ?? '' }));
    setEditingMap(prev => ({ ...prev, [userId]: false }));
  };

  const ClientListView = () => {
    if (loading) return <div>Loading...</div>;
    if (!clients.length) return <div>No clients assigned.</div>;

    // safe formatter: if value is 'YYYY-MM-DD' treat it as a date string (no TZ)
    const fmtDate = (v) => {
      if (!v) return '-';
      const s = String(v).split('T')[0];
      const parts = s.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        const [y, m, d] = parts;
        return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
      }
      const dt = new Date(v);
      if (Number.isNaN(dt.getTime())) return String(v).slice(0, 10);
      return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
    };

    const buildPhotoUrl = (photo) => {
      if (!photo) return null;
      if (String(photo).startsWith('http')) return photo;
      return `${apiUrl}/${String(photo).replace(/^\/+/, '')}`;
    };

    return (
      <div style={{ maxWidth: 980, margin: '0 auto', width: '100%' }}>
        <div className={styles.tableWrapper}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8 }}>SL No</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Name</th>
                <th style={{ textAlign: 'left', padding: 8 }}>TIN Number</th>
                <th style={{ textAlign: 'left', padding: 8, width: 180, maxWidth: 220 }}>NBR Password</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Mobile</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Date Joined</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c, i) => {
                const photo = c.profile_picture ?? c.avatar ?? c.photo ?? c.picture ?? null;
                const photoUrl = buildPhotoUrl(photo);
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f6f6f6' }}>
                    <td style={{ padding: 8 }}>{i + 1}</td>

                    {/* Name column: avatar + name/email */}
                    <td style={{ padding: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {photoUrl ? (
                          <img src={photoUrl} alt="avatar" style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 8 }} />
                        ) : (
                          <div style={{ width: 42, height: 42, background: '#eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, color: '#7b8794', fontWeight: 700 }}>
                            { (c.name || c.email || '').charAt(0).toUpperCase() || '-' }
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 700 }}>{c.name || c.email}</div>
                          <div style={{ fontSize: 12, color: '#556' }}>{c.email || '-'}</div>
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: 8 }}>{c.tin ?? c.tax_id ?? '-'}</td>
                    <td style={{ padding: 8, width: 180, maxWidth: 220 }}>
                      {!editingMap[c.id] ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.nbr_password ?? c.nbrPassword ?? c.password ?? '-'}
                          </div>
                          <button
                            title="Edit NBR password"
                            onClick={() => startEditing(c.id)}
                            className={styles.editIcon}
                            aria-label="Edit NBR password"
                          >
                            ✎
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="NBR password"
                            value={nbrInputs[c.id] ?? ''}
                            onChange={(e) => setNbrInputs(prev => ({ ...prev, [c.id]: e.target.value }))}
                            style={{ padding: 6, borderRadius: 6, border: '1px solid #ddd', width: 140, boxSizing: 'border-box' }}
                          />
                          <button
                            onClick={() => updateNbrPassword(c.id)}
                            disabled={!!savingMap[c.id]}
                            className={styles.saveBtn}
                            style={{ opacity: savingMap[c.id] ? 0.7 : 1, padding: '6px 8px' }}
                          >
                            {savingMap[c.id] ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => cancelEditing(c.id)}
                            style={{ padding: '6px 8px', borderRadius: 6, background: '#eee', color: '#333', border: 'none', cursor: 'pointer' }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>

                    <td style={{ padding: 8 }}>{c.mobile || '-'}</td>
                    <td style={{ padding: 8 }}>{fmtDate(c.created_at)}</td>
                    <td style={{ padding: 8 }}>
                      <button onClick={() => setSelectedClientId(c.id)}>View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  let content = null;
  if (section === 'clients') {
    content = selectedClientId ? (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3>Client Dashboard</h3>
          <button onClick={() => setSelectedClientId(null)}>Close</button>
        </div>
        <UserDashboard viewUserId={selectedClientId} />
      </div>
    ) : (
      <ClientListView />
    );
  } else if (section === 'clientDashboard') content = <div><h3>Client Dashboard</h3><DocumentList /></div>;
  else if (section === 'payments') content = <div><h3>Payment Info</h3><p>Summary for assigned clients</p></div>;
  else content = <div><h3>Settings</h3></div>;

  // remove extra top/right gap when viewing a selected client from the client list
  const isViewingClient = section === 'clients' && !!selectedClientId;
  const mainStyle = {
    marginLeft: SIDEBAR_WIDTH,
    // flush directly under the navbar (no extra top gap)
    marginTop: NAVBAR_HEIGHT,
    // when viewing a client remove right padding so content can use full width
    padding: isViewingClient ? '8px 0 20px 0' : 20,
    paddingRight: isViewingClient ? 0 : 20,
    // adjust minHeight to account for removed extra gap
    minHeight: isViewingClient ? `calc(100vh - ${NAVBAR_HEIGHT}px)` : `calc(100vh - ${NAVBAR_HEIGHT + 40}px)`,
    borderRadius: 8,
    background: '#f6f8fb'
  };

  return (
    <div>
      <ConsultantSidebar
        section={section}
        setSection={setSection}
      />
      <main style={mainStyle}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 6px 18px rgba(12,20,40,0.06)' }}>
            {content}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConsultantDashboard;